import { useState, useEffect } from 'react';

/**
 * Performance Sentinel System v3 - Singleton Edition
 * 
 * Why: Having every component run its own performance monitor and recovery interval
 * was causing a massive resource leak (N frame loops and N intervals).
 * Now, a single global loop monitors performance and notifies all subscribers.
 */

// Global State
let globalPanic = false;
let violationCount = 0;
const VIOLATION_THRESHOLD = 3;
let lastViolationTime = 0;
const COOLDOWN_MS = 15000; // Increased to 15s for better stability

// Config
const THRESHOLD_FPS = 15;
const FRAME_SAMPLE_COUNT = 60;
const MAX_FRAME_TIME = 300;

let globalPanicListeners: Array<(panic: boolean) => void> = [];
let isMonitoring = false;
let lastFrameTime = performance.now();
let frameTimes: number[] = [];

const notifyListeners = () => {
    globalPanicListeners.forEach(listener => listener(globalPanic));
};

// singleton interval/raf ids
let recoveryIntervalId: number | null = null;
let rafId: number | null = null;

const stopGlobalMonitor = () => {
    if (recoveryIntervalId) {
        clearInterval(recoveryIntervalId);
        recoveryIntervalId = null;
    }
    if (rafId) {
        cancelAnimationFrame(rafId);
        rafId = null;
    }
    isMonitoring = false;
};

// Check for existing instance on window (HMR cleanup)
if (typeof window !== 'undefined') {
    const w = window as any;
    if (w.__PERFORMANCE_SENTINEL_STOP__) {
        w.__PERFORMANCE_SENTINEL_STOP__();
    }
    w.__PERFORMANCE_SENTINEL_STOP__ = stopGlobalMonitor;
}

const startGlobalMonitor = () => {
    if (isMonitoring) return;
    isMonitoring = true;

    // 1. One Recovery Interval for everyone
    recoveryIntervalId = window.setInterval(() => {
        if (globalPanic && (Date.now() - lastViolationTime > COOLDOWN_MS)) {
            console.info('PERFORMANCE SENTINEL: Performance stabilized globally.');
            globalPanic = false;
            violationCount = 0;
            notifyListeners();
        }
    }, 2000);

    // 2. One Frame Loop for everyone
    const monitor = (time: number) => {
        if (!isMonitoring) return; // Exit if stopped

        const delta = time - lastFrameTime;
        lastFrameTime = time;

        // Skip massive spikes if they happen rarely (e.g. tab switch)
        if (delta > 1000) {
            rafId = requestAnimationFrame(monitor);
            return;
        }

        // Check for hang
        if (delta > MAX_FRAME_TIME) {
            violationCount++;
            lastViolationTime = Date.now();
        }

        // Track FPS
        frameTimes.push(delta);
        if (frameTimes.length > FRAME_SAMPLE_COUNT) {
            frameTimes.shift();
            const avg = frameTimes.reduce((a, b) => a + b, 0) / frameTimes.length;
            if (1000 / avg < THRESHOLD_FPS) {
                violationCount++;
                lastViolationTime = Date.now();
            }
        }

        // Trigger Panic if violations accumulate
        if (violationCount >= VIOLATION_THRESHOLD && !globalPanic) {
            console.warn('PERFORMANCE SENTINEL: Safety mode active.');
            globalPanic = true;
            notifyListeners();
        }

        rafId = requestAnimationFrame(monitor);
    };

    rafId = requestAnimationFrame(monitor);
};

export function usePerformanceSentinel() {
    const [isPanic, setIsPanic] = useState(globalPanic);

    useEffect(() => {
        // Start monitoring if not already
        startGlobalMonitor();

        const listener = (panic: boolean) => setIsPanic(panic);
        globalPanicListeners.push(listener);

        return () => {
            globalPanicListeners = globalPanicListeners.filter(l => l !== listener);
            // We keep the loop running even if no hooks are active to maintain global state,
            // or we could add logic to stop it if listeners.length === 0.
        };
    }, []);

    return { isPanic };
}
